<template>
  <div :class="['dashboard', { 'dark-mode': isDarkMode }]">
    <!-- Botão hamburguer para mobile -->
    <button class="btn-menu-toggle" @click="toggleSidebar">
      {{ sidebarAberta ? '✕' : '☰' }}
    </button>

    <!-- Overlay para fechar menu no mobile -->
    <div
      v-if="sidebarAberta"
      class="sidebar-overlay"
      @click="sidebarAberta = false"
    ></div>

    <!-- MENU LATERAL -->
    <aside :class="['sidebar', { 'sidebar-aberta': sidebarAberta, 'sidebar-colapsada': sidebarColapsada }]">
      <div class="logo-container" @click="sidebarColapsada ? toggleColapsar() : null">
        <img
          v-if="sidebarColapsada"
          :src="isDarkMode ? logoIconDark : logoIcon"
          alt="Logo"
          class="logo-icon"
        />
        <img
          v-else
          :src="isDarkMode ? logoNutriBrunaDark : logoNutriBruna"
          alt="Logo Nutri Bruna Barbosa"
          class="logo"
        />
      </div>

      <!-- Botão colapsar (desktop) -->
      <button class="btn-colapsar" :title="sidebarColapsada ? 'Expandir menu' : 'Recolher menu'" @click="toggleColapsar">
        {{ sidebarColapsada ? '»' : '«' }}
      </button>

      <!-- Menu de Navegação -->
      <nav class="menu-nav">
        <!-- Dashboard -->
        <div
          :class="['menu-item', { 'menu-ativo': menuAtivo === 'dashboard' }]"
          :title="sidebarColapsada ? 'Dashboard' : ''"
          @click="selecionarMenu('dashboard')"
        >
          <span class="menu-icon">📊</span>
          <span v-show="!sidebarColapsada" class="menu-label">Dashboard</span>
        </div>

        <!-- Pacientes (com submenu) -->
        <div class="menu-item menu-parent" :title="sidebarColapsada ? 'Pacientes' : ''" @click="sidebarColapsada ? toggleColapsar() : toggleSubmenu('pacientes')">
          <span class="menu-icon">👥</span>
          <span v-show="!sidebarColapsada" class="menu-label">Pacientes</span>
          <span v-show="!sidebarColapsada" class="menu-arrow">{{ submenuAberto.pacientes ? '▼' : '▶' }}</span>
        </div>

        <!-- Submenu Pacientes -->
        <div v-show="submenuAberto.pacientes && !sidebarColapsada" class="submenu">
          <div
            :class="['submenu-item', { 'submenu-ativo': menuAtivo === 'consultar' }]"
            @click="selecionarMenu('consultar')"
          >
            <span class="submenu-icon">🔍</span>
            <span>Consultar</span>
          </div>
          <div
            :class="['submenu-item', { 'submenu-ativo': menuAtivo === 'cadastrar' }]"
            @click="selecionarMenu('cadastrar')"
          >
            <span class="submenu-icon">➕</span>
            <span>Cadastrar</span>
          </div>
          <div
            :class="['submenu-item', { 'submenu-ativo': menuAtivo === 'importar' }]"
            @click="selecionarMenu('importar')"
          >
            <span class="submenu-icon">📥</span>
            <span>Importar</span>
          </div>
          <div
            :class="['submenu-item', { 'submenu-ativo': menuAtivo === 'exportar' }]"
            @click="selecionarMenu('exportar')"
          >
            <span class="submenu-icon">📤</span>
            <span>Exportar</span>
          </div>
        </div>

        <!-- Notificações -->
        <div
          :class="['menu-item', { 'menu-ativo': menuAtivo === 'notificacoes' }]"
          :title="sidebarColapsada ? 'Notificações' : ''"
          @click="selecionarMenu('notificacoes')"
        >
          <span class="menu-icon">📨</span>
          <span v-show="!sidebarColapsada" class="menu-label">Notificações</span>
        </div>

        <!-- Configurações -->
        <div class="menu-item" :title="sidebarColapsada ? 'Configurações' : ''" @click="selecionarMenu('configuracoes')">
          <span class="menu-icon">⚙️</span>
          <span v-show="!sidebarColapsada" class="menu-label">Configurações</span>
        </div>
      </nav>

      <!-- Área inferior do sidebar -->
      <div class="sidebar-footer">
        <!-- Botão para alternar entre temas -->
        <button class="btn-theme" :title="sidebarColapsada ? (isDarkMode ? 'Modo Claro' : 'Modo Escuro') : ''" @click="toggleDarkMode">
          <span>{{ isDarkMode ? "☀️" : "🌙" }}</span>
          <span v-show="!sidebarColapsada">{{ isDarkMode ? " Modo Claro" : " Modo Escuro" }}</span>
        </button>

        <!-- Botão de Logout -->
        <button ref="btnLogout" class="btn-logout" :title="sidebarColapsada ? 'Sair' : ''" @click="logoutComAnimacao">
          <span v-show="sidebarColapsada">🚪</span>
          <span v-show="!sidebarColapsada">Sair</span>
        </button>
      </div>
    </aside>

    <!-- CONTEÚDO PRINCIPAL -->
    <main class="conteudo">
      <DashboardStats v-if="menuAtivo === 'dashboard'" ref="dashboardStatsRef" :is-dark-mode="isDarkMode" />
      <Notificacoes v-else-if="menuAtivo === 'notificacoes'" />
      <PacientesTable v-else ref="pacientesTableRef" />
    </main>

    <!-- Modal de Importação -->
    <ImportModal
      :mostrar="mostrarImportModal"
      @fechar="mostrarImportModal = false"
      @importado="aoImportar"
    />

    <!-- Modal de Configurações -->
    <ConfiguracoesModal
      :mostrar="mostrarConfigModal"
      @fechar="mostrarConfigModal = false"
      @preferencias-atualizadas="aoAtualizarPreferencias"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import Swal from "sweetalert2";
import PacientesTable from "@/components/PacientesTable.vue";
import ImportModal from "@/components/ImportModal.vue";
import ConfiguracoesModal from "@/components/ConfiguracoesModal.vue";
import DashboardStats from "@/components/DashboardStats.vue";
import Notificacoes from "@/views/Notificacoes.vue";
import { resetAuthState } from "@/router";
import logoNutriBruna from "@/assets/logo-nutribruna.svg";
import logoNutriBrunaDark from "@/assets/logo-nutribruna-dark.svg";
import logoIcon from "@/assets/logo-icon.svg";
import logoIconDark from "@/assets/logo-icon-dark.svg";

const router = useRouter();

// Referências para os componentes
const pacientesTableRef = ref(null);
const dashboardStatsRef = ref(null);

// Modais
const mostrarImportModal = ref(false);
const mostrarConfigModal = ref(false);

// Estado do tema
const isDarkMode = ref(localStorage.getItem("darkMode") === "true");

// Estado do menu
const sidebarAberta = ref(false);
const sidebarColapsada = ref(localStorage.getItem("sidebarColapsada") === "true");
const menuAtivo = ref('dashboard');
const submenuAberto = ref({
  pacientes: true
});

// Toggle colapsar sidebar (desktop)
const toggleColapsar = () => {
  sidebarColapsada.value = !sidebarColapsada.value;
  localStorage.setItem("sidebarColapsada", sidebarColapsada.value);
};

// Toggle sidebar (mobile)
const toggleSidebar = () => {
  sidebarAberta.value = !sidebarAberta.value;
};

// Toggle submenu
const toggleSubmenu = (menu) => {
  submenuAberto.value[menu] = !submenuAberto.value[menu];
};

// Selecionar menu
const selecionarMenu = (menu) => {
  // Fecha sidebar no mobile
  if (window.innerWidth < 768) {
    sidebarAberta.value = false;
  }

  // Configurações: apenas abre o modal, sem mudar o menu ativo
  if (menu === 'configuracoes') {
    mostrarConfigModal.value = true;
    return;
  }

  // Cadastrar: abre modal de cadastro e mantém na consulta
  if (menu === 'cadastrar') {
    menuAtivo.value = 'consultar';
    setTimeout(() => {
      if (pacientesTableRef.value) {
        pacientesTableRef.value.abrirCadastro();
      }
    }, 100);
    return;
  }

  // Importar: abre modal de importação e mantém na consulta
  if (menu === 'importar') {
    menuAtivo.value = 'consultar';
    mostrarImportModal.value = true;
    return;
  }

  // Exportar: executa exportação e mantém na consulta
  if (menu === 'exportar') {
    menuAtivo.value = 'consultar';
    exportarPacientes();
    return;
  }

  // Outros menus: muda o menu ativo normalmente
  menuAtivo.value = menu;
};

// Exportar pacientes para CSV
const exportarPacientes = async () => {
  try {
    Swal.fire({
      title: 'Exportando...',
      text: 'Preparando arquivo CSV',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const response = await axios.get('/api/pacientes/export', {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    });

    // Criar link de download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pacientes_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    Swal.fire({
      icon: 'success',
      title: 'Exportado!',
      text: 'Arquivo CSV baixado com sucesso',
      timer: 2000,
      showConfirmButton: false
    });
  } catch (error) {
    if (!error._handled) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao exportar',
        text: 'Não foi possível exportar os dados'
      });
    }
  }
};

// Callback após importação
const aoImportar = () => {
  mostrarImportModal.value = false;
  Swal.fire({
    icon: 'success',
    title: 'Importação concluída!',
    text: 'Os dados foram importados com sucesso',
    timer: 2000,
    showConfirmButton: false
  });
  if (pacientesTableRef.value) {
    pacientesTableRef.value.recarregar();
  }
};

// Callback ao atualizar preferências
const aoAtualizarPreferencias = (prefs) => {
  if (prefs.tema === 'escuro') {
    isDarkMode.value = true;
  } else {
    isDarkMode.value = false;
  }
};

// Alternar entre os temas
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  localStorage.setItem("darkMode", isDarkMode.value);
};

// Watch para aplicar/remover classe dark-mode no body
watch(isDarkMode, (newValue) => {
  if (newValue) {
    document.body.classList.add("dark-mode");
  } else {
    document.body.classList.remove("dark-mode");
  }
}, { immediate: true });

// Aplica o tema salvo ao carregar a página
onMounted(() => {
  const darkModeSaved = localStorage.getItem("darkMode") === "true";
  isDarkMode.value = darkModeSaved;
});

// Referência do botão logout
const btnLogout = ref(null);

// Logout com animação
const logoutComAnimacao = async () => {
  // Adicionar animação
  if (btnLogout.value) {
    btnLogout.value.classList.add('btn-animating');
  }

  // Aguardar a animação antes de fazer logout
  setTimeout(async () => {
    await logout();
  }, 350);
};

// Logout
const logout = async () => {
  try {
    await axios.post('/api/logout', {}, {
      headers: { Accept: 'application/json' }
    });
  } catch {
    // Mesmo com erro, redireciona para login
  }
  resetAuthState();
  router.push("/login");
};
</script>

<style scoped>
/* === Estrutura principal === */
.dashboard {
  display: flex;
  height: 100vh;
  transition: background 0.3s, color 0.3s;
}

.dark-mode {
  background: #121212;
  color: #e0e0e0;
}

/* === Botão Menu Mobile === */
.btn-menu-toggle {
  display: none;
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1001;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 8px;
  width: 44px;
  height: 44px;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.dark-mode .btn-menu-toggle {
  background: #388e3c;
}

/* === Overlay Mobile === */
.sidebar-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* === Sidebar === */
.sidebar {
  width: 190px;
  min-width: 190px;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  transition: background 0.3s, transform 0.3s;
  z-index: 1000;
}

.dark-mode .sidebar {
  background: #1e1e1e;
  color: #ffffff;
}

/* === Logo === */
.logo-container {
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
}

.dark-mode .logo-container {
  border-bottom-color: #333;
}

.logo {
  width: 100%;
  max-width: 140px;
  height: auto;
}

.logo-icon {
  width: 32px;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s;
}

.logo-icon:hover {
  transform: scale(1.1);
}

/* === Botão Colapsar === */
.btn-colapsar {
  display: none;
  position: absolute;
  right: -14px;
  top: 60px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #4caf50;
  background: #fff;
  color: #4caf50;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  z-index: 1001;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.btn-colapsar:hover {
  background: #4caf50;
  color: white;
  transform: scale(1.1);
  box-shadow: 0 3px 12px rgba(76, 175, 80, 0.4);
}

.dark-mode .btn-colapsar {
  background: #1e1e1e;
  border-color: #4caf50;
  color: #4caf50;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.dark-mode .btn-colapsar:hover {
  background: #4caf50;
  color: white;
  box-shadow: 0 3px 12px rgba(76, 175, 80, 0.5);
}

/* === Sidebar Colapsada === */
.sidebar-colapsada {
  width: 70px !important;
  min-width: 70px !important;
}

.sidebar-colapsada .logo-container {
  padding: 10px 8px;
}

.sidebar-colapsada .menu-item {
  justify-content: center;
  padding: 12px 10px;
}

.sidebar-colapsada .menu-icon {
  margin-right: 0;
}

.sidebar-colapsada .sidebar-footer {
  padding: 10px;
}

.sidebar-colapsada .btn-theme,
.sidebar-colapsada .btn-logout {
  padding: 10px;
  font-size: 16px;
}

/* === Menu de Navegação === */
.menu-nav {
  flex: 1;
  padding: 10px 0;
  overflow-y: auto;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  user-select: none;
}

.menu-item:hover {
  background: #e8e8e8;
}

.dark-mode .menu-item:hover {
  background: #2a2a2a;
}

.menu-parent {
  font-weight: 500;
}

.menu-ativo {
  background: #e8f5e9;
  border-left: 3px solid #4caf50;
  font-weight: 500;
}

.dark-mode .menu-ativo {
  background: #1b4332;
  border-left-color: #4caf50;
}

.menu-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.menu-disabled:hover {
  background: transparent;
}

.menu-icon {
  font-size: 18px;
  margin-right: 12px;
  width: 24px;
  text-align: center;
}

.menu-label {
  flex: 1;
  font-size: 14px;
}

.menu-arrow {
  font-size: 10px;
  color: #888;
}

.menu-badge {
  font-size: 10px;
  background: #888;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
}

/* === Submenu === */
.submenu {
  background: #f0f0f0;
  border-left: 3px solid #4caf50;
  margin-left: 20px;
}

.dark-mode .submenu {
  background: #252525;
  border-left-color: #4caf50;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 13px;
}

.submenu-item:hover {
  background: #e0e0e0;
}

.dark-mode .submenu-item:hover {
  background: #333;
}

.submenu-ativo {
  background: #d4edda;
  color: #155724;
  font-weight: 500;
}

.dark-mode .submenu-ativo {
  background: #1b4332;
  color: #95d5b2;
}

.submenu-icon {
  font-size: 14px;
  margin-right: 10px;
  width: 20px;
  text-align: center;
}

.submenu-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.submenu-disabled:hover {
  background: transparent !important;
}

.dark-mode .submenu-disabled:hover {
  background: transparent !important;
}

.submenu-badge {
  font-size: 9px;
  background: #888;
  color: white;
  padding: 2px 5px;
  border-radius: 8px;
  margin-left: auto;
}

/* === Sidebar Footer === */
.sidebar-footer {
  padding: 12px;
  border-top: 1px solid #e0e0e0;
}

.dark-mode .sidebar-footer {
  border-top-color: #333;
}

/* === Botões === */
.btn-theme,
.btn-logout {
  width: 100%;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  padding: 7px 10px;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-theme {
  background: #444;
  color: #fff;
  margin-bottom: 6px;
}

.btn-theme:hover {
  background: #666;
}

.dark-mode .btn-theme {
  background: #f1f1f1;
  color: #333;
}

.dark-mode .btn-theme:hover {
  background: #ddd;
}

.btn-logout {
  background: #dc3545;
  color: #fff;
  font-size: 11px;
  transition: background 0.3s, transform 0.15s ease-out, box-shadow 0.15s ease-out;
}

.btn-logout:hover {
  background: #c82333;
}

.btn-logout:active {
  transform: scale(0.92);
  box-shadow: 0 2px 10px rgba(220, 53, 69, 0.5);
}

/* Animação de clique para botão Sair */
@keyframes btn-logout-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.88);
    box-shadow: 0 0 15px rgba(220, 53, 69, 0.6);
  }
  100% {
    transform: scale(1);
  }
}

.btn-logout.btn-animating {
  animation: btn-logout-pulse 0.35s ease-out;
}

/* === Conteúdo === */
.conteudo {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  transition: background 0.3s, color 0.3s;
}

.dark-mode .conteudo {
  background: #181818;
  color: #f1f1f1;
}

/* === Tabela (estilos herdados) === */
.dark-mode .tabela-container {
  background: #1e1e1e;
  border: 1px solid #444;
}

.dark-mode .tabela th {
  background: #333 !important;
  color: #fff !important;
}

.dark-mode .tabela td {
  background: #252525 !important;
  color: #e0e0e0 !important;
}

.dark-mode .tabela th,
.dark-mode .tabela td {
  background-color: #1e1e1e !important;
  color: #f1f1f1 !important;
}

.dark-mode .btn-visualizar {
  border-color: #4a90e2;
  color: #4a90e2;
}

.dark-mode .btn-editar {
  border-color: #4caf50;
  color: #4caf50;
}

.dark-mode .btn-excluir {
  border-color: #e74c3c;
  color: #e74c3c;
}

/* === RESPONSIVO - Mobile === */
@media (max-width: 768px) {
  .btn-menu-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sidebar-overlay {
    display: block;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    transform: translateX(-100%);
  }

  .sidebar-aberta {
    transform: translateX(0);
  }

  .conteudo {
    padding: 60px 8px 10px 8px;
  }

  /* Sidebar footer - tap targets maiores */
  .btn-theme,
  .btn-logout {
    min-height: 44px;
    font-size: 14px;
  }

  .menu-item {
    min-height: 44px;
  }

  .submenu-item {
    min-height: 44px;
  }
}

/* === Desktop - Botão colapsar visível === */
@media (min-width: 769px) {
  .sidebar {
    position: relative;
    overflow: visible;
  }

  .btn-colapsar {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* === Desktop grande === */
@media (min-width: 1200px) {
  .sidebar:not(.sidebar-colapsada) {
    width: 210px;
    min-width: 210px;
  }

  .sidebar:not(.sidebar-colapsada) .menu-item {
    padding: 14px 20px;
  }

  .sidebar:not(.sidebar-colapsada) .menu-label {
    font-size: 15px;
  }
}
</style>
